/**
 * <ls-gallery-lightbox> — full-size image viewer for section-ls-gallery.
 *
 * Wraps the gallery grid. Clicking a tile's zoom button opens a native
 * <dialog> showing the full image, its title and a counter, with prev/next
 * navigation. Esc, the close button and a backdrop click all dismiss it.
 *
 * Slides are collected at open time from tiles that are currently visible, so
 * "Load more" images only become reachable once they have been revealed.
 *
 * Expected markup (built by section-ls-gallery.liquid):
 *   <ls-gallery-lightbox data-label-close="…" data-label-previous="…"
 *                        data-label-next="…" data-label-counter="…">
 *     <div class="lsgal__grid">
 *       <div class="lsgal__item" data-full="…" data-title="…">
 *         …image…
 *         <button data-lsgal-open>…</button>
 *       </div>
 *     </div>
 *   </ls-gallery-lightbox>
 */
if (!customElements.get('ls-gallery-lightbox')) {
  customElements.define(
    'ls-gallery-lightbox',
    class LsGalleryLightbox extends HTMLElement {
      connectedCallback() {
        this.slides = [];
        this.index = 0;
        this.opener = null;
        this.dialog = null;

        this.onGridClick = this.onGridClick.bind(this);
        this.onDialogClick = this.onDialogClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onClose = this.onClose.bind(this);

        this.addEventListener('click', this.onGridClick);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onGridClick);
        if (this.dialog) {
          this.dialog.removeEventListener('click', this.onDialogClick);
          this.dialog.removeEventListener('keydown', this.onKeydown);
          this.dialog.removeEventListener('close', this.onClose);
          this.dialog.remove();
          this.dialog = null;
        }
      }

      onGridClick(event) {
        const trigger = event.target.closest('[data-lsgal-open]');
        if (!trigger || !this.contains(trigger)) return;

        const item = trigger.closest('.lsgal__item');
        if (!item) return;

        event.preventDefault();
        this.open(item, trigger);
      }

      /* Only tiles the visitor can actually see — "Load more" keeps the rest
         display: none, and navigating into them would be disorienting. */
      collectSlides() {
        const items = Array.from(this.querySelectorAll('.lsgal__item[data-full]'));
        return items.filter((item) => !item.classList.contains('lsgal__item--hidden'));
      }

      open(item, trigger) {
        this.slides = this.collectSlides();
        this.index = this.slides.indexOf(item);
        if (this.index < 0) return;

        this.opener = trigger;
        this.render();
        this.update();
        this.dialog.showModal();
      }

      render() {
        if (this.dialog) return;

        const dialog = document.createElement('dialog');
        dialog.className = 'lsgal-lightbox';
        dialog.innerHTML = `
          <div class="lsgal-lightbox__inner">
            <button type="button" class="lsgal-lightbox__btn lsgal-lightbox__btn--close" data-lsgal-close>
              <span class="visually-hidden">${this.dataset.labelClose || ''}</span>
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
            </button>
            <button type="button" class="lsgal-lightbox__btn lsgal-lightbox__btn--prev" data-lsgal-prev>
              <span class="visually-hidden">${this.dataset.labelPrevious || ''}</span>
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <figure class="lsgal-lightbox__figure">
              <img class="lsgal-lightbox__img" alt="">
              <figcaption class="lsgal-lightbox__caption">
                <span class="lsgal-lightbox__title"></span>
                <span class="lsgal-lightbox__counter"></span>
              </figcaption>
            </figure>
            <button type="button" class="lsgal-lightbox__btn lsgal-lightbox__btn--next" data-lsgal-next>
              <span class="visually-hidden">${this.dataset.labelNext || ''}</span>
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
          </div>
        `;

        this.img = dialog.querySelector('.lsgal-lightbox__img');
        this.titleEl = dialog.querySelector('.lsgal-lightbox__title');
        this.counterEl = dialog.querySelector('.lsgal-lightbox__counter');
        this.prevBtn = dialog.querySelector('[data-lsgal-prev]');
        this.nextBtn = dialog.querySelector('[data-lsgal-next]');

        dialog.addEventListener('click', this.onDialogClick);
        dialog.addEventListener('keydown', this.onKeydown);
        dialog.addEventListener('close', this.onClose);

        this.appendChild(dialog);
        this.dialog = dialog;
      }

      update() {
        const item = this.slides[this.index];
        if (!item) return;

        const title = item.dataset.title || '';
        this.img.src = item.dataset.full;
        this.img.alt = title;
        this.titleEl.textContent = title;

        /* Liquid renders the translation with %1 / %2 standing in for the
           numbers, so the wording stays localised. */
        const template = this.dataset.labelCounter || '';
        this.counterEl.textContent = template
          .replace('%1', String(this.index + 1))
          .replace('%2', String(this.slides.length));

        const single = this.slides.length < 2;
        this.prevBtn.hidden = single;
        this.nextBtn.hidden = single;
      }

      step(delta) {
        if (this.slides.length < 2) return;
        const total = this.slides.length;
        this.index = (this.index + delta + total) % total;
        this.update();
      }

      onDialogClick(event) {
        if (event.target.closest('[data-lsgal-close]')) {
          this.dialog.close();
          return;
        }
        if (event.target.closest('[data-lsgal-prev]')) {
          this.step(-1);
          return;
        }
        if (event.target.closest('[data-lsgal-next]')) {
          this.step(1);
          return;
        }
        /* ::backdrop is not a click target, so a click that lands on the
           dialog itself means the visitor clicked outside the content. */
        if (event.target === this.dialog) this.dialog.close();
      }

      onKeydown(event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        }
      }

      /* <dialog> handles Esc itself, so restore focus from the close event
         rather than from the close button. */
      onClose() {
        if (this.opener && this.opener.isConnected) this.opener.focus();
        this.opener = null;
        this.img.removeAttribute('src');
      }
    }
  );
}
